import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Entry } from '@h2h/shared';
import { AppShell, Content } from '../../components/ui/AppShell';
import { Placeholder } from '../../components/ui/Placeholder';
import { Swords } from '../../components/ui/icons';
import { useAuth } from '../../context/AuthContext';
import { useHaptics } from '../../hooks/useHaptics';
import { useCancelChallenge, useDuel, useLeagues, useMyDuels, useRespondToChallenge } from '../../hooks/useQueries';
import { LiveDuelSheet } from '../match-detail/LiveDuelSheet';
import { LiveFixtureSheet } from '../match-detail/LiveFixtureSheet';
import { ActiveDuelView } from './ActiveDuelView';
import { BackBar } from './BackBar';
import { ChallengeSent } from './ChallengeSent';
import { IncomingChallenge } from './IncomingChallenge';

export default function HeadToHeadScreen() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { duelId } = useParams<{ duelId: string }>();
  const { uid } = useAuth();
  const { data, isLoading } = useDuel(duelId, true);
  const { data: allDuels } = useMyDuels(uid);
  const { data: leagues } = useLeagues();
  const respond = useRespondToChallenge(uid);
  const cancel = useCancelChallenge(uid);
  const [liveFixtureId, setLiveFixtureId] = useState<string | null>(null);

  const fixtureMap = useMemo(
    () => new Map((data?.fixtures ?? []).map((f) => [f.fixtureId, f])),
    [data?.fixtures]
  );

  const myEntry = useMemo((): Entry | null => {
    if (!data?.round) return null;
    const { duel, challenger, opponent } = data;
    const meIsChallenger = challenger.userId === uid;
    const me = meIsChallenger ? challenger : opponent;
    return {
      userId: me.userId,
      leagueId: duel.leagueId,
      roundId: duel.roundId,
      groupId: '',
      tier: 0,
      predictions: me.predictions as Entry['predictions'],
      duelPicks: (me.duelPicks ?? [])
        .filter((p): p is typeof p & { pick: 'A' | 'B' } => !p.locked && !!p.pick)
        .map((p) => ({
          duelId: p.duelId,
          pick: p.pick,
          confidenceWeight: p.confidenceWeight ?? 0
        })),
      submittedAt: 0
    };
  }, [data, uid]);

  const league = useMemo(
    () => leagues?.find((l) => l.leagueId === data?.duel.leagueId),
    [leagues, data?.duel.leagueId]
  );

  if (isLoading || !data) {
    return (
      <AppShell>
        <Content>
          <BackBar onBack={() => navigate(-1)} label="Friends" eyebrow="Head-to-head duel" />
          <Placeholder Icon={Swords} title={isLoading ? 'Loading duel…' : 'Duel not found'} body="This head-to-head isn’t available." />
        </Content>
      </AppShell>
    );
  }

  const { duel, round, challenger, opponent } = data;
  const isCycling = data.sport === 'CYCLING';
  const friendsRoute = `/play/${duel.leagueId}/friends`;
  const goFriends = () => navigate(friendsRoute);
  const meIsChallenger = challenger.userId === uid;
  const me = meIsChallenger ? challenger : opponent;
  const rival = meIsChallenger ? opponent : challenger;

  // Season series between this pair (resolved duels in this league).
  const series = (allDuels ?? []).filter(
    (d) => d.status === 'RESOLVED' && d.participantIds.includes(rival.userId) && d.leagueId === duel.leagueId
  );
  const myWins = series.filter((d) => d.winnerId === uid).length;
  const theirWins = series.filter((d) => d.winnerId === rival.userId).length;

  const roundOpen = round?.status === 'OPEN';
  const iHaveEntry = isCycling ? (me.duelPicks?.length ?? 0) > 0 : me.predictions.length > 0;
  const goToPicks = () => {
    impact('medium');
    navigate(`/play/${duel.leagueId}?round=${duel.roundId}`);
  };

  // ── Pre-acceptance + terminal states (the challenge flow) ───────────────────
  if (duel.status === 'PENDING' && meIsChallenger) {
    return (
      <ChallengeSent
        data={data}
        rival={rival}
        roundOpen={roundOpen}
        iHaveEntry={iHaveEntry}
        onBack={goFriends}
        onMakePicks={goToPicks}
        onCancel={() => {
          impact('light');
          cancel.mutate(duel.duelId, { onSuccess: goFriends });
        }}
        cancelling={cancel.isPending}
      />
    );
  }

  if (duel.status === 'PENDING' && !meIsChallenger) {
    return (
      <IncomingChallenge
        data={data}
        rival={rival}
        myWins={myWins}
        theirWins={theirWins}
        onBack={goFriends}
        onAccept={() => {
          impact('medium');
          respond.mutate({ duelId: duel.duelId, accept: true });
        }}
        onDecline={() => {
          impact('light');
          respond.mutate({ duelId: duel.duelId, accept: false }, { onSuccess: goFriends });
        }}
        busy={respond.isPending}
      />
    );
  }

  if (duel.status === 'DECLINED' || duel.status === 'CANCELLED' || duel.status === 'EXPIRED') {
    const copy = {
      DECLINED: { title: 'Challenge declined', body: `${rival.displayName} declined this duel. Maybe next round.` },
      CANCELLED: { title: 'Challenge withdrawn', body: 'This challenge was cancelled before it was accepted.' },
      EXPIRED: { title: 'Challenge expired', body: 'The round locked before this duel was accepted.' }
    }[duel.status];
    return (
      <AppShell>
        <Content>
          <BackBar onBack={goFriends} label="Friends" eyebrow="Head-to-head duel" />
          <Placeholder Icon={Swords} title={copy.title} body={copy.body} />
        </Content>
      </AppShell>
    );
  }

  const showItsOn = duel.status === 'ACTIVE' && roundOpen;

  const myDuelById = new Map((me.duelPicks ?? []).map((p) => [p.duelId, p]));
  const rivalDuelById = new Map((rival.duelPicks ?? []).map((p) => [p.duelId, p]));
  const stageStarted = !!round && round.status !== 'SCHEDULED' && round.status !== 'OPEN';

  const itemIds = isCycling
    ? [...(data.fixtures ?? [])]
        .sort((a, b) => {
          const wb = (myDuelById.get(b.fixtureId)?.confidenceWeight ?? 0) - (myDuelById.get(a.fixtureId)?.confidenceWeight ?? 0);
          return wb !== 0 ? wb : a.fixtureId.localeCompare(b.fixtureId);
        })
        .map((f) => f.fixtureId)
    : [...new Set([...me.predictions, ...rival.predictions].map((p) => p.fixtureId))].sort(
        (a, b) => (fixtureMap.get(a)?.kickoffAt ?? 0) - (fixtureMap.get(b)?.kickoffAt ?? 0)
      );

  const remaining = isCycling
    ? (data.fixtures ?? []).filter((f) => f.status !== 'SETTLED' && f.status !== 'VOID').length
    : itemIds.filter((id) => {
        const f = fixtureMap.get(id);
        return !f || f.status !== 'SETTLED';
      }).length;

  return (
    <>
      <ActiveDuelView
        data={data}
        me={me}
        rival={rival}
        myWins={myWins}
        theirWins={theirWins}
        itemIds={itemIds}
        fixtureMap={fixtureMap}
        myDuelById={myDuelById}
        rivalDuelById={rivalDuelById}
        remaining={remaining}
        showItsOn={showItsOn}
        iHaveEntry={iHaveEntry}
        stageStarted={stageStarted}
        onBack={() => {
          impact('light');
          goFriends();
        }}
        onGoToPicks={goToPicks}
        onOpenFixture={(fixtureId) => {
          impact('light');
          setLiveFixtureId(fixtureId);
        }}
      />

      {round &&
        myEntry &&
        (isCycling ? (
          <LiveDuelSheet
            openDuelId={liveFixtureId}
            onClose={() => setLiveFixtureId(null)}
            leagueId={duel.leagueId}
            round={round}
            fixtures={data.fixtures ?? []}
            entry={myEntry}
          />
        ) : (
          <LiveFixtureSheet
            openFixtureId={liveFixtureId}
            onClose={() => setLiveFixtureId(null)}
            leagueId={duel.leagueId}
            round={round}
            fixtures={data.fixtures ?? []}
            entry={myEntry}
            nationalTeams={league?.country === 'World'}
          />
        ))}
    </>
  );
}
